import React, { useEffect } from "react"
import { Error403, Error404 } from "./shared/components"
import {
  Link,
  useRouteError,
  isRouteErrorResponse,
  useLocation,
} from "react-router"
import { AlertMessage } from "./shared/components"
import { captureException } from "@sentry/react-router"
import { isRedirect } from "./shared/routingUtils"
import { isFirstAttemptForPathname } from "./shared/isFirstAttemptForPathname"
import {
  apiErrorCode,
  isCommonErrorStatus,
  type CommonErrorStatus,
} from "./shared/utils/errorResponses"

const codeToComponent: Record<CommonErrorStatus, React.ComponentType> = {
  "401": Error403,
  "403": Error403,
  "404": Error404,
  "410": Error404,
}

export function isModuleImportTypeError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    (error.message.includes("Importing a module script failed") ||
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Load failed"))
  )
}

export function CommonErrorBoundary() {
  const error = useRouteError()
  const { pathname } = useLocation()

  if (isRedirect(error)) throw error

  const code = isRouteErrorResponse(error)
    ? error.status.toString()
    : apiErrorCode(error)

  useEffect(() => {
    if (isModuleImportTypeError(error)) {
      if (isFirstAttemptForPathname("chunk-retry", pathname)) {
        console.warn("Failed to import module, forcing reload. Error:", error)
        window.location.reload()
        return
      }
      // sentryRouterOnError skips chunk-load failures so the first attempt can
      // reload silently; the ones that survive the reload are reported here.
      captureException(error, {
        tags: { errorBoundary: "CommonErrorBoundary" },
      })
    }
  }, [error, pathname])

  if (isCommonErrorStatus(code)) {
    const Component = codeToComponent[code]
    return <Component />
  }

  return <FallbackErrorComponent />
}

// a function intended to be used as the retry function on a react-query query
export function unlessCommonError(i: number, error: unknown) {
  const isCommonErrorCode = isCommonErrorStatus(apiErrorCode(error))
  const lessThanDefaultRetryCount = i < 3
  return !isCommonErrorCode && lessThanDefaultRetryCount
}

export function LayoutlessErrorBoundary() {
  return <FallbackErrorComponent />
}

function FallbackErrorComponent() {
  return (
    <div className="mx-8 my-4 ta-c">
      <AlertMessage>
        Something unexpected happened. Please try again.
      </AlertMessage>
      <Link to="/home" className="secondary-btn btn mb-3 mt-3">
        Go home
      </Link>
    </div>
  )
}
